/**
 * SetupStage — stage 1 of the decision page.
 *
 * The question, who makes the call, the deadline, background, who has a say,
 * who is kept informed, what matters (criteria), and the options. Every text
 * field autosaves on blur through `update_brief_setup`; criteria and options
 * save row by row. Nothing here is held only in the browser.
 */

import { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Field, TextInput, TextArea, Select } from '@/components/ds';
import {
  updateBrief,
  upsertCriteria,
  deleteCriteria,
  createOption,
  updateOption,
  deleteOption,
  type BriefSetupPatch,
} from '../../../services/brief/briefService';
import type { Brief, BriefCriteria, BriefOption } from '../../../types/brief';

const STANDARD_CRITERIA = [
  { name: 'Strategic fit', description: 'How well this lines up with what we said we are trying to do', weight: 4 },
  { name: 'Cost', description: 'People, money and time over the next year', weight: 3 },
  { name: 'Time to value', description: 'How long until the first result we can see', weight: 3 },
];

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function SaveHint({ state, error }: { state: SaveState; error: string | null }) {
  if (state === 'idle') return null;
  if (state === 'saving') return <span className="text-xs text-[var(--text-dim)]">Saving…</span>;
  if (state === 'saved') return <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">Saved</span>;
  return <span className="text-xs text-[var(--coral)]">{error ?? 'Not saved'}</span>;
}

/** A text control that saves its value on blur and says so. */
function AutosaveText({
  id,
  label,
  hint,
  value,
  placeholder,
  multiline,
  type,
  disabled,
  onSave,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  placeholder?: string;
  multiline?: boolean;
  type?: string;
  disabled?: boolean;
  onSave: (value: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState(value);
  const [state, setState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = async () => {
    if (draft === value) return;
    setState('saving');
    try {
      await onSave(draft);
      setState('saved');
      setTimeout(() => setState('idle'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Not saved');
      setState('error');
    }
  };

  return (
    <Field
      label={
        <span className="flex items-center justify-between gap-3">
          <span>{label}</span>
          <SaveHint state={state} error={error} />
        </span>
      }
      hint={hint}
      htmlFor={id}
    >
      {multiline ? (
        <TextArea id={id} value={draft} rows={3} placeholder={placeholder} disabled={disabled} onChange={(e) => setDraft(e.target.value)} onBlur={commit} />
      ) : (
        <TextInput id={id} type={type} value={draft} placeholder={placeholder} disabled={disabled} onChange={(e) => setDraft(e.target.value)} onBlur={commit} />
      )}
    </Field>
  );
}

export interface SetupStageProps {
  brief: Brief;
  criteria: BriefCriteria[];
  options: BriefOption[];
  onBriefChange: (brief: Brief) => void;
  onCriteriaChange: (criteria: BriefCriteria[]) => void;
  onOptionsChange: (options: BriefOption[]) => void;
  onError: (message: string) => void;
  /** Rendered at the end of the stage (the "Open for proposals" action lives on the page). */
  footer?: React.ReactNode;
}

export function SetupStage({ brief, criteria, options, onBriefChange, onCriteriaChange, onOptionsChange, onError, footer }: SetupStageProps) {
  const locked = brief.status === 'decided' || brief.status === 'archived';
  /** Options and what matters are fixed once proposals are being collected against them. */
  const frameLocked = brief.status !== 'draft';

  const save = (patch: BriefSetupPatch) => async () => {
    const next = await updateBrief(brief.id, patch);
    onBriefChange(next);
  };
  const saveText = (key: keyof BriefSetupPatch) => async (value: string) => {
    const next = await updateBrief(brief.id, { [key]: value } as BriefSetupPatch);
    onBriefChange(next);
  };

  // ── Criteria ───────────────────────────────────────────────────────────────
  const [criteriaDrafts, setCriteriaDrafts] = useState<Array<{ id?: string; name: string; description: string; weight: number }>>(
    criteria.map((c) => ({ id: c.id, name: c.name, description: c.description ?? '', weight: c.weight })),
  );
  useEffect(() => {
    setCriteriaDrafts(criteria.map((c) => ({ id: c.id, name: c.name, description: c.description ?? '', weight: c.weight })));
  }, [criteria]);

  const persistCriteria = async (rows: typeof criteriaDrafts) => {
    const named = rows.filter((r) => r.name.trim());
    if (named.length === 0) return;
    try {
      const saved = await upsertCriteria(
        brief.id,
        named.map((r, i) => ({ id: r.id, name: r.name, description: r.description, weight: r.weight, sort_order: i })),
      );
      onCriteriaChange(saved);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save what matters.');
    }
  };

  const useStandardCriteria = () => {
    const rows = STANDARD_CRITERIA.map((c) => ({ ...c }));
    setCriteriaDrafts(rows);
    void persistCriteria(rows);
  };

  const removeCriterion = async (index: number) => {
    const row = criteriaDrafts[index];
    const next = criteriaDrafts.filter((_, i) => i !== index);
    setCriteriaDrafts(next);
    if (row.id) {
      try {
        await deleteCriteria(row.id);
        onCriteriaChange(criteria.filter((c) => c.id !== row.id));
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Could not remove that.');
      }
    }
  };

  // ── Options ────────────────────────────────────────────────────────────────
  type OptionDraft = { id?: string; name: string; owner_name: string; owner_email: string };
  const [optionDrafts, setOptionDrafts] = useState<OptionDraft[]>(() => {
    const rows: OptionDraft[] = options.map((o) => ({ id: o.id, name: o.name, owner_name: o.owner_name ?? '', owner_email: o.owner_email ?? '' }));
    while (rows.length < 2) rows.push({ name: '', owner_name: '', owner_email: '' });
    return rows;
  });
  useEffect(() => {
    setOptionDrafts((prev) => {
      const rows: OptionDraft[] = options.map((o) => ({ id: o.id, name: o.name, owner_name: o.owner_name ?? '', owner_email: o.owner_email ?? '' }));
      // keep unsaved blank rows the person has open
      for (const p of prev) if (!p.id && (p.name || p.owner_name || p.owner_email)) rows.push(p);
      while (rows.length < 2) rows.push({ name: '', owner_name: '', owner_email: '' });
      return rows;
    });
  }, [options]);

  const persistOption = async (index: number) => {
    const row = optionDrafts[index];
    if (!row.name.trim()) return;
    try {
      if (row.id) {
        const existing = options.find((o) => o.id === row.id);
        if (existing && existing.name === row.name.trim() && (existing.owner_name ?? '') === row.owner_name.trim() && (existing.owner_email ?? '') === row.owner_email.trim()) return;
        const saved = await updateOption(row.id, { name: row.name.trim(), owner_name: row.owner_name.trim() || null, owner_email: row.owner_email.trim() || null });
        onOptionsChange(options.map((o) => (o.id === saved.id ? saved : o)));
      } else {
        const saved = await createOption(brief.id, { name: row.name, owner_name: row.owner_name, owner_email: row.owner_email, sort_order: index });
        setOptionDrafts((prev) => prev.map((r, i) => (i === index ? { ...r, id: saved.id } : r)));
        onOptionsChange([...options, saved]);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save that option.');
    }
  };

  const removeOption = async (index: number) => {
    const row = optionDrafts[index];
    setOptionDrafts((prev) => prev.filter((_, i) => i !== index));
    if (row.id) {
      try {
        await deleteOption(row.id);
        onOptionsChange(options.filter((o) => o.id !== row.id));
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Could not remove that option.');
      }
    }
  };

  const inputCls = 'w-full h-9 border border-[var(--rule-bright)] px-3 text-sm text-[var(--text-pri)] bg-[var(--surface)] placeholder:text-[var(--text-dim)] focus:outline-none focus:ring-2 focus:ring-[var(--slate)] focus:border-transparent disabled:opacity-60';

  return (
    <div className="space-y-8">
      {/* The question and the frame */}
      <div className="space-y-5">
        <AutosaveText id="setup-title" label="The question" value={brief.title} placeholder="Which CRM vendor do we pick?" disabled={locked} onSave={saveText('title')} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <AutosaveText id="setup-decider" label="Who makes the call?" value={brief.decision_authority ?? ''} placeholder="e.g. Priya" disabled={locked} onSave={saveText('decision_authority')} />
          <AutosaveText id="setup-deadline" label="Decide by" type="date" value={brief.deadline ?? ''} disabled={locked} onSave={saveText('deadline')} />
        </div>
        <AutosaveText id="setup-context" label="Background" hint="One paragraph anyone writing a proposal should read first. Optional." value={brief.context ?? ''} multiline disabled={locked} onSave={saveText('context')} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <AutosaveText id="setup-voice" label="Who has a say?" hint="The people whose input is on the record." value={brief.voice_holders ?? ''} placeholder="e.g. Product leads, Finance" disabled={locked} onSave={saveText('voice_holders')} />
          <AutosaveText id="setup-observers" label="Who is kept informed?" hint="They see it; they do not decide." value={brief.observers ?? ''} placeholder="e.g. Legal" disabled={locked} onSave={saveText('observers')} />
        </div>
      </div>

      {/* What matters */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-semibold text-sm text-[var(--text-pri)]" style={{ color: 'var(--text-pri)' }}>What matters</h3>
          {!frameLocked && criteriaDrafts.length === 0 && (
            <button type="button" onClick={useStandardCriteria} className="text-sm text-[var(--text-ter)] underline underline-offset-2">
              Not sure? Use the standard three
            </button>
          )}
        </div>
        <p className="text-sm text-[var(--text-sec)] mb-3">
          Each proposal is judged against these. Weight says how much each one counts, 1 to 5.
        </p>
        {frameLocked && criteriaDrafts.length === 0 && <p className="text-sm text-[var(--text-dim)]">None recorded.</p>}
        <div className="space-y-2">
          {criteriaDrafts.map((c, i) => (
            <div key={c.id ?? `new-${i}`} className="flex items-start gap-2">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_1fr_5rem] gap-2">
                <input
                  aria-label={`Criterion ${i + 1} name`}
                  className={inputCls}
                  value={c.name}
                  placeholder="e.g. Strategic fit"
                  disabled={frameLocked}
                  onChange={(e) => setCriteriaDrafts((prev) => prev.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))}
                  onBlur={() => persistCriteria(criteriaDrafts)}
                />
                <input
                  aria-label={`Criterion ${i + 1} description`}
                  className={inputCls}
                  value={c.description}
                  placeholder="What it means (optional)"
                  disabled={frameLocked}
                  onChange={(e) => setCriteriaDrafts((prev) => prev.map((r, j) => (j === i ? { ...r, description: e.target.value } : r)))}
                  onBlur={() => persistCriteria(criteriaDrafts)}
                />
                <Select
                  aria-label={`Criterion ${i + 1} weight`}
                  value={c.weight}
                  disabled={frameLocked}
                  onChange={(e) => {
                    const next = criteriaDrafts.map((r, j) => (j === i ? { ...r, weight: Number(e.target.value) } : r));
                    setCriteriaDrafts(next);
                    void persistCriteria(next);
                  }}
                >
                  {[1, 2, 3, 4, 5].map((w) => (
                    <option key={w} value={w}>Weight {w}</option>
                  ))}
                </Select>
              </div>
              {!frameLocked && (
                <button type="button" onClick={() => removeCriterion(i)} aria-label={`Remove criterion ${i + 1}`} className="h-9 w-9 flex items-center justify-center text-[var(--text-dim)] hover:text-[var(--coral)]">
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {!frameLocked && (
          <button
            type="button"
            onClick={() => setCriteriaDrafts((prev) => [...prev, { name: '', description: '', weight: 3 }])}
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)]"
          >
            <PlusIcon className="w-4 h-4" /> Add something that matters
          </button>
        )}
      </div>

      {/* Options */}
      <div>
        <h3 className="font-heading font-semibold text-sm text-[var(--text-pri)] mb-2" style={{ color: 'var(--text-pri)' }}>The options</h3>
        <p className="text-sm text-[var(--text-sec)] mb-3">
          At least two. Add the owner's email and the tool sends them their private link when you open for proposals.
        </p>
        <div className="space-y-2">
          {optionDrafts.map((o, i) => (
            <div key={o.id ?? `new-${i}`} className="flex items-start gap-2">
              <span className="flex-shrink-0 w-9 h-9 flex items-center justify-center font-mono text-xs text-[var(--text-ter)] bg-[var(--surface)]" aria-hidden="true">
                {String.fromCharCode(65 + i)}
              </span>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1.2fr] gap-2">
                <input
                  aria-label={`Option ${String.fromCharCode(65 + i)} name`}
                  className={inputCls}
                  value={o.name}
                  placeholder="Option name"
                  disabled={frameLocked}
                  onChange={(e) => setOptionDrafts((prev) => prev.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))}
                  onBlur={() => persistOption(i)}
                />
                <input
                  aria-label={`Option ${String.fromCharCode(65 + i)} owner name`}
                  className={inputCls}
                  value={o.owner_name}
                  placeholder="Owner (optional)"
                  disabled={frameLocked}
                  onChange={(e) => setOptionDrafts((prev) => prev.map((r, j) => (j === i ? { ...r, owner_name: e.target.value } : r)))}
                  onBlur={() => persistOption(i)}
                />
                <input
                  aria-label={`Option ${String.fromCharCode(65 + i)} owner email`}
                  className={inputCls}
                  type="email"
                  value={o.owner_email}
                  placeholder="Owner email (optional)"
                  disabled={frameLocked}
                  onChange={(e) => setOptionDrafts((prev) => prev.map((r, j) => (j === i ? { ...r, owner_email: e.target.value } : r)))}
                  onBlur={() => persistOption(i)}
                />
              </div>
              {!frameLocked && optionDrafts.length > 2 && (
                <button type="button" onClick={() => removeOption(i)} aria-label={`Remove option ${String.fromCharCode(65 + i)}`} className="h-9 w-9 flex items-center justify-center text-[var(--text-dim)] hover:text-[var(--coral)]">
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {!frameLocked && optionDrafts.length < 8 && (
          <button
            type="button"
            onClick={() => setOptionDrafts((prev) => [...prev, { name: '', owner_name: '', owner_email: '' }])}
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)]"
          >
            <PlusIcon className="w-4 h-4" /> Add an option
          </button>
        )}
      </div>

      {/* Classification, quietly */}
      {!locked && (
        <details className="text-sm">
          <summary className="cursor-pointer text-[var(--text-ter)] hover:text-[var(--text-pri)]">Tags and classification (optional)</summary>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <AutosaveText id="setup-tags" label="Tags" hint="Comma-separated." value={(brief.tags ?? []).join(', ')} onSave={async (v) => { await save({ tags: v.split(',').map((t) => t.trim()).filter(Boolean) })(); }} />
            <AutosaveText id="setup-scope-label" label="Team or unit" value={brief.scope_label ?? ''} placeholder="e.g. Product Engineering" onSave={saveText('scope_label')} />
          </div>
        </details>
      )}

      {footer}
    </div>
  );
}
